import CloseIcon from '@mui/icons-material/Close'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Collapse from '@mui/material/Collapse'
import FormHelperText from '@mui/material/FormHelperText'
import IconButton from '@mui/material/IconButton'
import Snackbar from '@mui/material/Snackbar'
import { Box } from '@mui/system'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { esES } from '@mui/x-date-pickers/locales'
import 'dayjs/locale/es'
import { validate } from 'features/solicitudes/libs/FieldResquestValidator'
import {
  consultaInicial,
  executeFieldAction,
  selectCamposSolicitud,
  selectDatosSolicitud,
  setCampoError,
  setCampoValidando,
} from 'features/solicitudes/solicitudesSlice'
import { SnackbarProvider, closeSnackbar, enqueueSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  getValoresConsulta,
  processValidationResult,
  todosLosPadresTienenValor,
} from '../../libs/SolicitudesHelpers'
import LabelComponent from '../LabelComponent'
import ValidatingText from '../ValidatingText'
import CustomDateTimeComponent from './CustomDateTimeComponent'

function DateRangeComponent({ idpk }) {
  const dispatch = useDispatch()

  const MAX_NOTIF = 5

  const campos = useSelector(selectCamposSolicitud)

  const datos = useSelector(selectDatosSolicitud)

  const [open, setOpen] = useState(false)
  const [desde, setDesde] = useState({})
  const [hasta, setHasta] = useState({})
  const [encontradoDesde, setEncontradoDesde] = useState(false)
  const [valorDesde, setValorDesde] = useState()
  const [valorHasta, setValorHasta] = useState()
  const [validationError, setValidationError] = useState('')
  const [alertOpen, setAlertOpen] = useState(false)

  if (!encontradoDesde) {
    let encontrado = false
    for (const c of campos) {
      if (c.idpk === idpk) {
        setDesde(c)
        setEncontradoDesde(true)
        encontrado = true
      }
      if (encontrado && c.tipo_dato === 'DH2') {
        setHasta(c)
        break
      }
    }
  }

  useEffect(() => {
    setValorDesde(datos[desde.idpk])
    setValorHasta(datos[hasta.idpk])
  }, [datos, desde, hasta])

  useEffect(() => {
    // Lanzamos las queries iniciales para campos dependientes solo si:
    // 1. El campo tiene valor
    // 2. El campo dependiente necesita inicialización (inicializando === 'P')
    // 3. El campo dependiente NO está siendo inicializado por inicializaSolicitud (inicializando !== 'R')
    campos.forEach((c) => {
      if (
        c.depende_de &&
        c.depende_de.includes(desde.codigo_campo) &&
        valorDesde &&
        c.inicializando === 'P' &&
        todosLosPadresTienenValor(c, campos, datos)
      ) {
        const valores = getValoresConsulta(datos, campos)
        dispatch(consultaInicial({ idpk: c.idpk, valores }))
      }
    })
    desde.acciones.forEach((action) => {
      if (
        action.valor_referencia === valorDesde ||
        (action.valor_referencia === 'NOT_NULL' && valorDesde) ||
        (action.valor_referencia === 'NULL' && !valorDesde)
      ) {
        dispatch(executeFieldAction({ action }))
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valorDesde])

  useEffect(() => {
    // Lanzamos las queries iniciales para campos dependientes solo si:
    // 1. El campo tiene valor
    // 2. El campo dependiente necesita inicialización (inicializando === 'P')
    // 3. El campo dependiente NO está siendo inicializado por inicializaSolicitud (inicializando !== 'R')
    campos.forEach((c) => {
      if (
        c.depende_de &&
        c.depende_de.includes(hasta.codigo_campo) &&
        valorHasta &&
        c.inicializando === 'P'
      ) {
        const valores = getValoresConsulta(datos, campos)
        dispatch(consultaInicial({ idpk: c.idpk, valores }))
      }
    })
    hasta.acciones.forEach((action) => {
      if (
        action.valor_referencia === valorHasta ||
        (action.valor_referencia === 'NOT_NULL' && valorHasta) ||
        (action.valor_referencia === 'NULL' && !valorHasta)
      ) {
        dispatch(executeFieldAction({ action }))
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valorHasta])

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return
    }
    setOpen(false)
  }

  const handleError = (open, message, idpk) => {
    setAlertOpen(open)
    setValidationError(message)
    dispatch(setCampoError({ idpk: idpk, error: open }))
  }

  const handleChangeDesde = async (nuevo_valor) => {
    try {
      if (valorHasta && nuevo_valor.isAfter(valorHasta)) {
        const msg = 'La fecha del campo hasta debe ser igual o posterior a la fecha del campo desde'
        enqueueSnackbar(msg, 'error')
        setValorDesde(null)
        handleError(true, msg, desde.idpk)
        return false
      }

      setValorDesde(nuevo_valor)

      // Activar estado de validación
      dispatch(setCampoValidando({ idpk: desde.idpk, validando: true }))

      const result = await validate(desde, nuevo_valor, campos, datos)
      processValidationResult(result, desde, nuevo_valor, dispatch)
    } catch (error) {
      setValorDesde(null)
      handleError(true, error.message, desde.idpk)
    } finally {
      // Desactivar estado de validación
      dispatch(setCampoValidando({ idpk: desde.idpk, validando: false }))
    }
  }
  const handleChangeHasta = async (nuevo_valor) => {
    try {
      if (valorDesde) {
        if (valorDesde.isAfter(nuevo_valor)) {
          const msg =
            'La fecha del campo hasta debe ser igual o posterior a la fecha del campo desde'
          enqueueSnackbar(msg, 'error')
          handleError(true, msg, hasta.idpk)
          setValorHasta(null)
          return false
        }

        setValorHasta(nuevo_valor)

        // Activar estado de validación
        dispatch(setCampoValidando({ idpk: hasta.idpk, validando: true }))

        const result = await validate(hasta, nuevo_valor, campos, datos)
        processValidationResult(result, hasta, nuevo_valor, dispatch)
      } else {
        setValorHasta(null)

        const msg = 'Debe seleccionar el campo desde primero.'
        handleError(true, msg, hasta.idpk)
        enqueueSnackbar(msg, 'error')
      }
    } catch (error) {
      setValorHasta(null)
      handleError(true, error.message, hasta.idpk)
    } finally {
      // Desactivar estado de validación
      dispatch(setCampoValidando({ idpk: hasta.idpk, validando: false }))
    }
  }

  if (desde && hasta) {
    const datesFrom = validate(desde, true, campos, datos)
    const datesTo = validate(hasta, true, campos, datos)

    return (
      <LocalizationProvider
        localeText={esES.components.MuiLocalizationProvider.defaultProps.localeText}
        dateAdapter={AdapterDayjs}
        adapterLocale="es"
      >
        <Snackbar open={open} autoHideDuration={6000} onClose={handleClose}>
          <Alert onClose={handleClose} severity="error" sx={{ width: '100%' }}>
            No puede seleccionar este campo hasta haber cubierto el anterior
          </Alert>
        </Snackbar>

        <SnackbarProvider
          maxSnack={MAX_NOTIF + 1}
          autoHideDuration={6000}
          action={(snackbarId) => (
            <IconButton onClick={() => closeSnackbar(snackbarId)} aria-label="cerrar alerta">
              <CloseRoundedIcon />
            </IconButton>
          )}
          anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
          variant={'error'}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
          <Box
            sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', gap: 1 }}
          >
            <Box sx={{ flexGrow: 1 }}>
              <LabelComponent text={desde.etiqueta} obligatorio={desde.obligatorio} />

              <CustomDateTimeComponent
                campo={{
                  ...desde,
                  informativo: desde.informativo === 'S' || desde.validando ? 'S' : 'N',
                }}
                dates={datesFrom}
                valor={valorDesde}
                onChange={(v) => handleChangeDesde(v)}
              />
              {desde.validando && (
                <FormHelperText>
                  <ValidatingText />
                </FormHelperText>
              )}
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <LabelComponent text={hasta.etiqueta} obligatorio={hasta.obligatorio} />

              <CustomDateTimeComponent
                campo={{
                  ...hasta,
                  informativo: hasta.informativo === 'S' || hasta.validando ? 'S' : 'N',
                }}
                dates={datesTo}
                valor={valorHasta}
                onChange={(v) => handleChangeHasta(v)}
              />
              {hasta.validando && (
                <FormHelperText>
                  <ValidatingText />
                </FormHelperText>
              )}
            </Box>
          </Box>
          <Collapse in={alertOpen}>
            <Alert
              action={
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setAlertOpen(false)
                  }}
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              }
              sx={{ mb: 2 }}
              severity="error"
            >
              <AlertTitle>Error</AlertTitle>
              {validationError}
            </Alert>
          </Collapse>
        </Box>
      </LocalizationProvider>
    )
  }
}

export default DateRangeComponent
